import { APP_INITIALIZER, ErrorHandler, NgModule, isDevMode } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { ClipboardModule } from 'ngx-clipboard';
import { TranslateModule } from '@ngx-translate/core';
import { InlineSVGModule } from 'ng-inline-svg-2';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AuthService } from './modules/auth/services/auth.service';
import { SweetAlert2Module } from '@sweetalert2/ngx-sweetalert2';
import { AuthInterceptor } from './modules/auth/services/auth.interceptor';
import { CoreModule } from './core';
import { SharedModule } from './shared/shared.module';
import { ToastrModule, ToastrService } from 'ngx-toastr';
import { LocalizationService } from './shared/services/localization.service';
import { PermissionService } from './core/services/permission.service';
import { Router } from '@angular/router';
import * as Sentry from "@sentry/angular";
import { CacheInterceptor } from './core/interceptors/cache.interceptor';
import { LanguageInterceptor } from './core/interceptors/language.interceptor';
import { SentryHttpMonitoringInterceptor } from './core/interceptors/sentry-http-monitoring.interceptor';

import { NgxIndexedDBModule } from 'ngx-indexed-db';
import { dbConfig } from './core/cache/indexedb';
import { environment } from 'src/environments/environment';
import { JwtService } from './core/services/jwt.service';

export function initPermissions(
  permSvc: PermissionService,
  auth: AuthService,
  jwtService: JwtService
) {
  let userId: string | null = null;
  const token = auth.getAccessToken();
  if (token) {
    userId = jwtService.getUserId(token);
  }

  return () => {
    return userId
      ? permSvc.loadPermissions(userId)
      : Promise.resolve();
  };
}

/** Ensures TraceService is constructed early so Router events attach to Sentry spans. / تفعيل مبكر لخدمة التتبع */
function initSentryTracing(_trace: Sentry.TraceService): () => void {
  return () => undefined;
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    SharedModule,
    TranslateModule.forRoot(),
    HttpClientModule,
    ClipboardModule,
    AppRoutingModule,
    InlineSVGModule.forRoot(),
    NgbModule,
    SweetAlert2Module.forRoot(),
    ToastrModule.forRoot({
      timeOut: 2000, // Display time (ms)
      positionClass: 'toast-bottom-left', // Position of the toast
      preventDuplicates: false, // Prevent duplicate toasts
      closeButton: true, // Show close button
      progressBar: true, // Show progress bar
      progressAnimation: 'increasing', // Progress bar animation type
      extendedTimeOut: 1000, // Extended display time (ms)
      tapToDismiss: true, // Click to dismiss
    }),
    CoreModule,

    NgxIndexedDBModule.forRoot(dbConfig),
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: initSentryTracing,
      deps: [Sentry.TraceService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initPermissions,
      deps: [PermissionService, AuthService, JwtService],
      multi: true
    },
    { provide: HTTP_INTERCEPTORS, useClass: SentryHttpMonitoringInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: CacheInterceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LanguageInterceptor,
      multi: true,
      deps: [LocalizationService],
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
      deps: [AuthService, LocalizationService, ToastrService, PermissionService, Router],
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule { }